import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom"; // for header link
import api from "../api/http";

/* Small UI helpers */
function Popup({ open, type = "success", title, message, onClose }) {
  if (!open) return null;
  const isSuccess = type === "success";
  const color = isSuccess ? "text-green-700 bg-green-50 ring-green-200" : "text-red-700 bg-red-50 ring-red-200";
  const icon = isSuccess ? "fas fa-check-circle" : "fas fa-exclamation-triangle";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-md rounded-xl ring-1 ${color} shadow-lg`}>
        <div className="p-6">
          <div className="flex items-start gap-3">
            <i className={`${icon} text-2xl mt-1`} />
            <div>
              <div className="font-semibold text-lg">{title || (isSuccess ? "Success" : "Error")}</div>
              <div className="mt-1 text-sm">{message}</div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-cyan-400 text-white hover:bg-cyan-500"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, title, message, confirmText = "Confirm", onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <i className="fas fa-trash text-red-500 text-2xl mt-1" />
            <div>
              <div className="font-semibold text-lg">{title}</div>
              <div className="mt-1 text-sm text-gray-700">{message}</div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Main component */
const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Popup
  const [popup, setPopup] = useState({ open: false, type: "success", title: "", message: "" });

  // Delete confirmation
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });

  const BASE = (api?.defaults?.baseURL || "").replace(/\/+$/, "");
  const ORIGIN = BASE.replace(/\/+$/, "").replace(/\/api(?:\/)?$/, ""); // ✅ fixed

  const showPopup = (message, type = "success", title = "") =>
    setPopup({ open: true, type, title: title || (type === "success" ? "Success" : "Error"), message });

  const cap = (s) => (typeof s === "string" ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  const fmtDate = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return dt.toISOString().slice(0, 10);
    } catch {
      return d;
    }
  };

  const makeTelHref = (phone) => {
    if (!phone) return "#";
    const normalized = String(phone).replace(/[^+\d]/g, "");
    return `tel:${normalized}`;
  };

  // Load contacts from API
  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = {
        q: searchTerm || undefined,
        status:
          statusFilter === "all"
            ? undefined
            : statusFilter === "in_progress"
            ? "In Progress"
            : cap(statusFilter), // New, Replied, Completed
      };

      const res = await api.get("/contact-messages", { params });
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const normalized = raw.map((c) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone,
        message: c.message,
        date: fmtDate(c.date || c.created_at),
        status: cap(c.status || "New"),
      }));
      setContacts(normalized);
    } catch (err) {
      console.error(err);
      showPopup("Failed to load contacts.", "error");
    } finally {
           setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // initial load

  const filteredContacts = useMemo(() => {
    const term = (searchTerm || "").toLowerCase();
    let rows = contacts;
    if (statusFilter !== "all") {
      const target = statusFilter === "in_progress" ? "In Progress" : cap(statusFilter);
      rows = rows.filter((c) => (c.status || "").toLowerCase() === target.toLowerCase());
    }
    if (!term) return rows;
    return rows.filter(
      (contact) =>
        contact.name.toLowerCase().includes(term) ||
        contact.email.toLowerCase().includes(term) ||
        (contact.message || "").toLowerCase().includes(term)
    );
  }, [contacts, searchTerm, statusFilter]);

  const handleSearchSubmit = async (e) => {
    e?.preventDefault?.();
    await loadContacts();
  };

  const handleViewMessage = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleMarkAsPending = async (id) => {
    try {
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status: "In Progress" } : c)));
      await api.patch(`/contact-messages/${id}`, { status: "In Progress" });
      showPopup("Message marked as pending (in progress).", "success");
    } catch (err) {
      console.error(err);
      showPopup("Failed to set pending status.", "error");
      loadContacts();
    }
  };

  const handleMarkAsComplete = async (id) => {
    try {
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status: "Completed" } : c)));
      await api.patch(`/contact-messages/${id}`, { status: "Completed" });
      setIsModalOpen(false);
      showPopup("Message marked as completed successfully!", "success");
    } catch (err) {
      console.error(err);
      showPopup("Failed to update status.", "error");
      loadContacts();
    }
  };

  const askDelete = (id, name) => setConfirm({ open: true, id, name: name || "this contact" });

  const confirmDelete = async () => {
    const { id } = confirm;
    try {
      setContacts((curr) => curr.filter((c) => c.id !== id));
      await api.delete(`/contact-messages/${id}`);
      setConfirm({ open: false, id: null, name: "" });
      showPopup("Contact deleted successfully!", "success");
    } catch (err) {
      console.error(err);
      setConfirm({ open: false, id: null, name: "" });
      showPopup("Failed to delete contact.", "error");
      loadContacts();
    }
  };

  const exportContacts = async () => {
    try {
      const url = `${ORIGIN}/api/contact-messages/export?format=csv`;
      const head = await fetch(url, { method: "HEAD" });
      if (head.ok) {
        window.open(url, "_blank");
        return;
      }
      throw new Error("No server export route");
    } catch {
      const headers = ["ID", "Name", "Email", "Phone", "Date", "Status", "Message"];
      const rows = contacts.map((c) => [
        c.id,
        `"${(c.name || "").replace(/"/g, '""')}"`,
        `"${(c.email || "").replace(/"/g, '""')}"`,
        `"${(c.phone || "").replace(/"/g, '""')}"`,
        c.date || "",
        c.status || "",
        `"${(c.message || "").replace(/"/g, '""')}"`,
      ]);
      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      link.href = dl;
      link.setAttribute("download", `contact_messages_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dl);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "bg-cyan-400 text-white";
      case "Replied":
        return "bg-blue-500 text-white";
      case "In Progress":
        return "bg-amber-500 text-white";
      case "Completed":
        return "bg-green-500 text-white";
      default:
        return "bg-gray-300 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Popup (success/error) */}
      <Popup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={() => setPopup((p) => ({ ...p, open: false }))}
      />

      {/* Delete confirmation */}
      <ConfirmModal
        open={confirm.open}
        title="Delete contact?"
        message={`This will permanently remove ${confirm.name}. You can't undo this.`}
        confirmText="Delete"
        onCancel={() => setConfirm({ open: false, id: null, name: "" })}
        onConfirm={confirmDelete}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header with button-link */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400">Contact Management</h1>
            <p className="text-gray-600 mt-2">Manage all customer inquiries and messages</p>
          </div>

          {/* Link Button (change path if needed) */}
          <Link
            to="/Admin/Projectsmanagement"
            className="px-6 py-3 bg-cyan-400 text-white rounded-lg shadow hover:bg-cyan-500 transition duration-200 flex items-center gap-2"
            title="Go project management page"
          >
            {/* ⬇️ Updated to a valid project-management icon (FA5). If you use FA6, use 'fa-diagram-project'. */}
            <i className="fas fa-project-diagram" aria-hidden="true" />
            Projects Management
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-cyan-100 text-cyan-400 mr-4">
                <i className="fas fa-envelope text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Messages</p>
                <h3 className="text-2xl font-bold text-gray-800">{loading ? "…" : contacts.length}</h3>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100 text-blue-500 mr-4">
                <i className="fas fa-check-circle text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {contacts.filter((c) => c.status === "Completed").length}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-amber-100 text-amber-500 mr-4">
                <i className="fas fa-clock text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {contacts.filter((c) => c.status === "In Progress").length}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100 text-red-500 mr-4">
                <i className="fas fa-exclamation-circle text-xl"></i>
              </div>
              <div>
                <p className="text-sm text-gray-500">New Messages</p>
                <h3 className="text-2xl font-bold text-gray-800">
                  {contacts.filter((c) => c.status === "New").length}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Filters + Export */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <form className="grid grid-cols-1 md:grid-cols-12 gap-4" onSubmit={handleSearchSubmit}>
            <div className="md:col-span-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search contacts by name, email or message..."
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status filter */}
            <div className="md:col-span-3">
              <select
                className="w-full py-3 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="replied">Replied</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="md:col-span-3 flex gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-200 flex items-center justify-center"
                title="Apply filters"
              >
                <i className="fas fa-filter mr-2"></i>
                Apply
              </button>
              <button
                type="button"
                onClick={exportContacts}
                className="flex-1 px-4 py-3 bg-cyan-400 text-white rounded-lg hover:bg-cyan-500 transition duration-200 flex items-center justify-center"
              >
                <i className="fas fa-download mr-2"></i>
                Export
              </button>
            </div>
          </form>
        </div>

        {/* Contacts Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-cyan-400">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && filteredContacts.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No contacts found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filteredContacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-bold">
                              {contact.name?.charAt(0)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {contact.email ? (
                          <a
                            href={`mailto:${contact.email}?subject=${encodeURIComponent(
                              "Re: Your inquiry with Pera Engineering"
                            )}`}
                            className="text-cyan-600 hover:underline"
                            title="Send an email"
                          >
                            {contact.email}
                          </a>
                        ) : (
                          <div className="text-sm text-gray-500">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {contact.phone ? (
                          <a
                            href={makeTelHref(contact.phone)}
                            className="text-cyan-600 hover:underline"
                            title="Call this number"
                          >
                            {contact.phone}
                          </a>
                        ) : (
                          <div className="text-sm text-gray-500">—</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{contact.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            contact.status
                          )}`}
                        >
                          {contact.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewMessage(contact)}
                          className="text-cyan-400 hover:text-cyan-600 mr-4"
                          title="View full message"
                        >
                          <i className="fas fa-eye mr-1"></i> View
                        </button>

                        <button
                          onClick={() => handleMarkAsPending(contact.id)}
                          className="text-amber-500 hover:text-amber-600 mr-4"
                          title="Mark as Pending (In Progress)"
                        >
                          <i className="fas fa-hourglass-half mr-1"></i> Pending
                        </button>

                        <button
                          onClick={() => handleMarkAsComplete(contact.id)}
                          className="text-green-500 hover:text-green-700 mr-4"
                          title="Mark as Completed"
                        >
                          <i className="fas fa-check mr-1"></i> Complete
                        </button>

                        <button
                          onClick={() => askDelete(contact.id, contact.name)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete message"
                        >
                          <i className="fas fa-trash mr-1"></i> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Message Modal */}
        {isModalOpen && selectedContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Message from {selectedContact.name}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                  aria-label="Close"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Name</h4>
                    <p className="mt-1 text-sm text-gray-900">{selectedContact.name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Email</h4>
                    {selectedContact.email ? (
                      <a
                        href={`mailto:${selectedContact.email}?subject=${encodeURIComponent(
                          "Re: Your inquiry with Pera Engineering"
                        )}`}
                        className="mt-1 text-sm text-cyan-600 hover:underline"
                      >
                        {selectedContact.email}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">—</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Phone</h4>
                    {selectedContact.phone ? (
                      <a
                        href={makeTelHref(selectedContact.phone)}
                        className="mt-1 text-sm text-cyan-600 hover:underline"
                      >
                        {selectedContact.phone}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">—</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date</h4>
                    <p className="mt-1 text-sm text-gray-900">{selectedContact.date}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500">Message</h4>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{selectedContact.message}</p>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    className="px-4 py-2 bg-amber-500 text-white rounded-md text-sm font-medium hover:bg-amber-600"
                    onClick={() => handleMarkAsPending(selectedContact.id)}
                  >
                    Mark Pending
                  </button>
                  <button
                    className="px-4 py-2 bg-cyan-400 text-white rounded-md text-sm font-medium hover:bg-cyan-500"
                    onClick={() => handleMarkAsComplete(selectedContact.id)}
                  >
                    Mark as Completed
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ContactManagement;
